import { Comments } from "@/components/Comments";
import { CustomHeader } from "@/components/CustomHeader";
import { PostCard } from "@/components/PostCard";
import { PostSkeleton } from "@/components/PostSkeleton";
import { RequestGroupAccess } from "@/components/RequestGroupAccess";
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { useGroupAccess } from "@/hooks/useGroupAccess";
import { usePost } from "@/hooks/usePost";
import { useThemeColor } from "@/hooks/useThemeColor";
import { router, useLocalSearchParams } from "expo-router";
import Head from "expo-router/head";
import React from "react";
import { useTranslation } from "react-i18next";
import { StyleSheet } from "react-native";
import { APP_NAME } from "@/utils/appMeta";

// Matches the feed's cap in app/(tabs)/index.web.tsx so line-length stays
// readable on desktop. No-op on native (screens are always narrower).
const CONTENT_MAX_WIDTH = 680;

function Chrome({
  documentTitle,
  title,
  children,
}: {
  documentTitle: string;
  title: string;
  children: React.ReactNode;
}) {
  // Match the feed: gray "sides" wrap a centered white content column so the
  // capped area reads as a distinct card, not a random ~680px slab of white.
  const stripesColor = useThemeColor({}, "stripes");
  const backgroundColor = useThemeColor({}, "background");
  return (
    <ThemedView style={{ flex: 1 }}>
      <Head>
        <title>{documentTitle}</title>
      </Head>
      <CustomHeader
        title={title}
        type="subtitle"
        variant="tab"
        showBackButton
      />
      <ThemedView style={[styles.sides, { backgroundColor: stripesColor }]}>
        <ThemedView style={[styles.contentCap, { backgroundColor }]}>
          {children}
        </ThemedView>
      </ThemedView>
    </ThemedView>
  );
}

export default function PostDetailsScreen() {
  const { postId } = useLocalSearchParams<{ postId: string }>();
  const { post, loading } = usePost(postId);
  const { t } = useTranslation();
  const {
    status: accessStatus,
    group: accessGroup,
    canRead,
  } = useGroupAccess(post?.groupId);
  const blockedFromGroup =
    !!post?.groupId && accessStatus !== "loading" && !canRead;

  const descriptionSnippet = post?.description?.trim();
  const briefSnippet =
    descriptionSnippet && descriptionSnippet.length > 60
      ? descriptionSnippet.slice(0, 60).trimEnd() + "…"
      : descriptionSnippet;
  const titleFragment = briefSnippet || post?.eventType;
  const docTitle = titleFragment
    ? t("PAGE_TITLE_POST", { title: titleFragment })
    : APP_NAME;

  // After the author deletes their post, unmount this screen before Firestore
  // surfaces the racy `permission-denied` from the still-live Comments listener
  // reading the now-missing parent doc.
  const handlePostDeleted = () => {
    if (router.canGoBack()) {
      router.back();
    } else {
      router.replace("/");
    }
  };

  if (loading) {
    return (
      <Chrome documentTitle={docTitle} title={t("POST_DETAILS")}>
        <PostSkeleton />
      </Chrome>
    );
  }

  if (!post) {
    return (
      <Chrome documentTitle={docTitle} title={t("POST_NOT_FOUND")}>
        <ThemedView style={styles.container}>
          <ThemedText
            testID="post-details-not-found"
            style={{ textAlign: "center" }}
          >
            {t("POST_NOT_FOUND")}
          </ThemedText>
        </ThemedView>
      </Chrome>
    );
  }

  if (blockedFromGroup) {
    return (
      <Chrome documentTitle={docTitle} title={t("POST_DETAILS")}>
        <RequestGroupAccess
          groupId={post.groupId!}
          groupName={accessGroup?.name ?? ""}
          pending={accessStatus === "request-pending"}
        />
      </Chrome>
    );
  }

  return (
    <Chrome documentTitle={docTitle} title={t("POST_DETAILS")}>
      <Comments
        postId={post.id}
        ListHeaderComponent={
          <PostCard
            post={post}
            isLCPCandidate
            onDeleted={handlePostDeleted}
          />
        }
        keyboardVerticalOffset={60}
      />
    </Chrome>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignContent: "center",
    justifyContent: "center",
    padding: 16,
  },
  sides: {
    flex: 1,
    alignItems: "center",
  },
  contentCap: {
    flex: 1,
    width: "100%",
    maxWidth: CONTENT_MAX_WIDTH,
  },
});
